// Audit log — globální historie událostí (manuální runs, cron runs, Pravidla updates).
// Role: owner + manager. Backend: /api/audit-log (Netlify Blobs store "audit-log").

import { useState, useMemo, useEffect } from "react";
import GradientHeader from "../components/GradientHeader.jsx";
import { Stranka } from "../components/ui.jsx";
import { useUzkaObrazovka } from "../lib/obrazovka.js";
import { SkeletonTable } from "../components/Skeleton.jsx";
import { useApiData } from "../lib/api.js";
import { formatDateTime as fmtDateTime } from "../lib/format.js";
import { getCurrentUser } from "../lib/auth.js";
import {
  History, CheckCircle2, XCircle, Clock, Filter, X, Pause, Play, RefreshCw,
  Zap, Settings, FileText, User, Bot, Download, ChevronDown, ShieldCheck,
} from "lucide-react";

// GDPR 19.10.2026: kdo si otevřel zdravotní data klientky. Vidí jen owner (backend
// je ostatním nevrací). Volba se načítá ze serveru zvlášť za celé zvolené období,
// protože běžný výpis 200 záznamů zaplní crony za pár hodin.
const ZDRAVOTNI = "zdravotni-data";

// Auto-refresh interval: 30s. Audit log je po většinu času append-only,
// ale pri běžícím manuálním runu user chce vidět nový entry hned.
const AUTO_REFRESH_MS = 30_000;

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    return fmtDateTime(iso) || iso;
  } catch {
    return iso;
  }
}

function formatDuration(ms) {
  if (ms === null || ms === undefined) return "—";
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  return `${Math.floor(ms / 60_000)} min ${Math.floor((ms % 60_000) / 1000)} s`;
}

function todayStr(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().substring(0, 10);
}

const SOURCE_LABELS = {
  "automatizace-run": "Manuální spuštění",
  "scheduled": "Cron běh",
  "pravidla": "Pravidla a výjimky",
  "audit-log": "Audit log",
  "klienti": "Klienti",
  [ZDRAVOTNI]: "Zdravotní data (kdo otevřel)",
};

// Source ikony + barvy pro vizuální rozlišení v tabulce
const SOURCE_VISUAL = {
  "automatizace-run": { icon: User, bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200" },
  "scheduled":         { icon: Bot, bg: "bg-brand-50", text: "text-brand-700", border: "border-brand-200" },
  "pravidla":          { icon: Settings, bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" },
  "audit-log":         { icon: FileText, bg: "bg-slate-50", text: "text-slate-700", border: "border-slate-200" },
  "klienti":           { icon: User, bg: "bg-teal-50", text: "text-teal-700", border: "border-teal-200" },
  [ZDRAVOTNI]:         { icon: ShieldCheck, bg: "bg-slate-50", text: "text-slate-700", border: "border-slate-200" },
};
function getSourceVisual(source) {
  return SOURCE_VISUAL[source] || { icon: Zap, bg: "bg-slate-50", text: "text-slate-700", border: "border-slate-200" };
}

// Server query obsahuje JEN dateFrom / dateTo / limit. Ostatní filtry (source / user / action / result)
// se aplikují klient-side v useMemo `filtered`, takže klik na KPI dlaždici nebo změna selectu
// nedělá server roundtrip a UI reaguje instantně.
function buildQuery(filters) {
  const params = new URLSearchParams();
  if (filters.dateFrom) params.set("dateFrom", filters.dateFrom);
  if (filters.dateTo) params.set("dateTo", filters.dateTo);
  if (filters.limit) params.set("limit", String(filters.limit));
  if (filters.sources.includes(ZDRAVOTNI)) params.set("source", ZDRAVOTNI);
  return params.toString();
}

export default function AuditLog() {
  const uzka = useUzkaObrazovka();
  const jeOwner = getCurrentUser()?.role === "owner";
  const [filters, setFilters] = useState({
    dateFrom: todayStr(-7),
    dateTo: todayStr(0),
    sources: [],   // multi-select: prazdne = vsechny
    user: "",
    action: "",
    result: "",
    limit: 200,
  });
  const [search, setSearch] = useState("");
  const [expandedRow, setExpandedRow] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [sourcesDropdownOpen, setSourcesDropdownOpen] = useState(false);

  const queryString = useMemo(() => buildQuery(filters), [filters]);
  const { data, loading, error, refetch, refreshing } = useApiData(`/api/audit-log?${queryString}`);

  // Auto-refresh kazdych 30s, pause pri inactive tabu (visibilitychange).
  useEffect(() => {
    if (!autoRefresh) return undefined;
    let t = null;
    const tick = () => {
      // Skip pokud document je hidden (user prepnul tab)
      if (document.visibilityState === "visible") {
        refetch();
      }
    };
    t = setInterval(tick, AUTO_REFRESH_MS);
    return () => { if (t) clearInterval(t); };
  }, [autoRefresh, refetch]);
  const entries = data?.entries || [];

  // Client-side filter: sources (multi) / user / action / result + full-text search.
  // Server query posílá JEN dateFrom/dateTo/limit, takže klik na KPI dlaždice
  // nebo změna selectu reaguje instantně bez backend roundtripu.
  const filtered = useMemo(() => {
    let out = entries;
    if (filters.sources.length > 0) out = out.filter((e) => filters.sources.includes(e.source));
    if (filters.user) out = out.filter((e) => e.user === filters.user);
    if (filters.action) out = out.filter((e) => e.action === filters.action);
    if (filters.result) out = out.filter((e) => e.result === filters.result);
    if (search.trim()) {
      const needle = search.toLowerCase().trim();
      out = out.filter((e) => JSON.stringify(e).toLowerCase().indexOf(needle) >= 0);
    }
    return out;
  }, [entries, filters.sources, filters.user, filters.action, filters.result, search]);

  // KPI: počty
  const kpis = useMemo(() => {
    return {
      total: entries.length,
      ok: entries.filter((e) => e.result === "OK").length,
      nok: entries.filter((e) => e.result === "NOK").length,
      manual: entries.filter((e) => e.source === "automatizace-run").length,
      cron: entries.filter((e) => e.source === "scheduled").length,
      pravidla: entries.filter((e) => e.source === "pravidla").length,
    };
  }, [entries]);

  const sources = useMemo(() => {
    const set = new Set(entries.map((e) => e.source).filter(Boolean));
    if (jeOwner) set.add(ZDRAVOTNI);
    return Array.from(set).sort();
  }, [entries, jeOwner]);

  const users = useMemo(() => {
    const set = new Set(entries.map((e) => e.user).filter(Boolean));
    return Array.from(set).sort();
  }, [entries]);

  const actions = useMemo(() => {
    const set = new Set(entries.map((e) => e.action).filter(Boolean));
    return Array.from(set).sort();
  }, [entries]);

  // Helper pro KPI dlaždice — toggle source v multi-array.
  function toggleSource(src) {
    setFilters((prev) => {
      const has = prev.sources.includes(src);
      // Zdravotní data se načítají zvlášť ze serveru, proto se s jinými zdroji nekombinují.
      const bezZdrav = prev.sources.filter((s) => s !== ZDRAVOTNI);
      const next = src === ZDRAVOTNI
        ? (has ? [] : [ZDRAVOTNI])
        : (has ? bezZdrav.filter((s) => s !== src) : [...bezZdrav, src]);
      return { ...prev, sources: next };
    });
  }

  function resetFilters() {
    setFilters({
      dateFrom: todayStr(-7),
      dateTo: todayStr(0),
      sources: [],
      user: "",
      action: "",
      result: "",
      limit: 200,
    });
    setSearch("");
  }

  // Export filtered zaznamu do CSV. Sloupce: cas, zdroj, akce, uzivatel, vysledek, doba (ms),
  // chyba, message, JSON details. Stahne soubor `audit-log-YYYY-MM-DD-HHMM.csv`.
  function exportToCsv() {
    if (filtered.length === 0) return;
    const headers = ["timestamp", "source", "action", "user", "result", "durationMs", "error", "message", "details"];
    const escape = (val) => {
      if (val === null || val === undefined) return "";
      const s = typeof val === "object" ? JSON.stringify(val) : String(val);
      // Escape uvozovky, obal celek do uvozovek pokud obsahuje carku/uvozovku/newline
      const needsQuote = /[",\n\r]/.test(s);
      const escaped = s.replace(/"/g, '""');
      return needsQuote ? `"${escaped}"` : escaped;
    };
    const rows = [headers.join(",")];
    for (const e of filtered) {
      rows.push([
        escape(e.timestamp),
        escape(e.source),
        escape(e.action),
        escape(e.user),
        escape(e.result),
        escape(e.durationMs),
        escape(e.error),
        escape(e.message),
        escape(e.details),
      ].join(","));
    }
    const csv = "﻿" + rows.join("\n"); // BOM pro Excel UTF-8
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().substring(0, 16).replace(/[:T-]/g, "").replace(/(\d{8})(\d{4})/, "$1-$2");
    a.href = url;
    a.download = `audit-log-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <Stranka>
      <GradientHeader
        title="Audit log"
        napoveda="Historie událostí v celé aplikaci. Eviduje ruční spuštění automatizací, kdo a kdy je spustil, automatické noční běhy včetně výsledku a statistik, a změny pravidel a výjimek. Záznamy se drží devadesát dní. Samo se to obnovuje každých třicet sekund, pokud je obnovování zapnuté."
        icon={History}
        loading={refreshing}
        akce={[
          {
            klic: "auto",
            ikona: autoRefresh ? Pause : Play,
            popis: autoRefresh ? "Obnovování každých 30 s je zapnuté, klepnutím vypneš" : "Obnovování je vypnuté, klepnutím zapneš",
            onClick: () => setAutoRefresh((v) => !v),
          },
          { klic: "obnovit", ikona: RefreshCw, popis: "Obnovit", onClick: refetch, disabled: refreshing },
        ]}
      />

      {/* KPI klikací dlaždice — kliknutím se aplikuje filtr (toggle).
          Filter source a result jsou nezávislé (lze kombinovat = 2 active dlaždice najednou).
          Active stav: sytější pozadí (bg-XXX-100), tlustší ring-4 + offset, lehký scale, vlevo dole "✓ Aktivní filtr".
          Mapování: OK/NOK → result; Manuální/Cron/Pravidla → source. Celkem = vyčistí oba. */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {[
          { label: "Celkem", value: kpis.total, icon: History, bgIdle: "bg-slate-50", bgActive: "bg-slate-100", text: "text-slate-700", textActive: "text-slate-900", border: "border-slate-200", ring: "ring-slate-500",
            isActive: !filters.result && filters.sources.length === 0,
            onClick: () => setFilters({ ...filters, result: "", sources: [] }) },
          { label: "OK", value: kpis.ok, icon: CheckCircle2, bgIdle: "bg-emerald-50", bgActive: "bg-emerald-100", text: "text-emerald-700", textActive: "text-emerald-900", border: "border-emerald-200", ring: "ring-emerald-500",
            isActive: filters.result === "OK",
            onClick: () => setFilters({ ...filters, result: filters.result === "OK" ? "" : "OK" }) },
          { label: "NOK", value: kpis.nok, icon: XCircle, bgIdle: "bg-rose-50", bgActive: "bg-rose-100", text: "text-rose-700", textActive: "text-rose-900", border: "border-rose-200", ring: "ring-rose-500",
            isActive: filters.result === "NOK",
            onClick: () => setFilters({ ...filters, result: filters.result === "NOK" ? "" : "NOK" }) },
          { label: "Manuální", value: kpis.manual, icon: User, bgIdle: "bg-blue-50", bgActive: "bg-blue-100", text: "text-blue-700", textActive: "text-blue-900", border: "border-blue-200", ring: "ring-blue-500",
            isActive: filters.sources.includes("automatizace-run"),
            onClick: () => toggleSource("automatizace-run") },
          { label: "Cron", value: kpis.cron, icon: Bot, bgIdle: "bg-brand-50", bgActive: "bg-brand-100", text: "text-brand-700", textActive: "text-brand-900", border: "border-brand-200", ring: "ring-brand-500",
            isActive: filters.sources.includes("scheduled"),
            onClick: () => toggleSource("scheduled") },
          { label: "Pravidla", value: kpis.pravidla, icon: Settings, bgIdle: "bg-amber-50", bgActive: "bg-amber-100", text: "text-amber-700", textActive: "text-amber-900", border: "border-amber-200", ring: "ring-amber-500",
            isActive: filters.sources.includes("pravidla"),
            onClick: () => toggleSource("pravidla") },
        ].map((k) => {
          const Icon = k.icon;
          return (
            <button
              key={k.label}
              type="button"
              onClick={k.onClick}
              className={`relative rounded-xl border p-3 text-left w-full transition-all hover:shadow-md cursor-pointer ${
                k.isActive
                  ? `${k.bgActive} ring-4 ring-offset-1 ${k.ring} border-transparent shadow-md scale-[1.02]`
                  : `${k.bgIdle} ${k.border} hover:brightness-95`
              }`}
            >
              <div className={`flex items-center gap-1.5 text-xs uppercase tracking-wide font-bold ${k.isActive ? k.textActive : k.text}`}>
                <Icon className="w-3.5 h-3.5" />
                {k.label}
              </div>
              <div className={`text-2xl font-bold mt-1 tabular-nums ${k.isActive ? k.textActive : k.text}`}>{k.value}</div>
              {k.isActive && (
                <div className={`absolute bottom-1.5 right-2 text-[11px] font-bold ${k.textActive} flex items-center gap-1`}>
                  <CheckCircle2 className="w-3 h-3" /> AKTIVNÍ
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Filtry */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-slate-600" />
          <h2 className="font-medium text-slate-700">Filtry</h2>
          <button
            onClick={exportToCsv}
            disabled={filtered.length === 0}
            className="btn btn-secondary ml-auto"
            title="Stáhnout filtrované záznamy jako CSV (Excel UTF-8)"
          >
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
          <button
            onClick={resetFilters}
            className="min-h-[44px] text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1 sm:min-h-0"
          >
            <X className="w-3 h-3" /> Reset
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <div>
            <label className="text-xs text-slate-500">Od</label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
              className="field w-full mt-1"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500">Do</label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
              className="field w-full mt-1"
            />
          </div>
          <div className="relative">
            <label className="text-xs text-slate-500">Zdroje</label>
            <button
              type="button"
              onClick={() => setSourcesDropdownOpen((v) => !v)}
              className={`min-h-[44px] w-full mt-1 text-sm border rounded px-2 py-1.5 text-left flex items-center justify-between gap-1 transition sm:min-h-0 ${
                filters.sources.length > 0 ? "bg-blue-50 text-blue-700 border-blue-300 font-semibold ring-1 ring-blue-200" : "bg-white border-slate-300 text-slate-600"
              }`}
            >
              <span className="truncate">
                {filters.sources.length === 0 ? "Všechny" :
                  filters.sources.length === 1 ? (SOURCE_LABELS[filters.sources[0]] || filters.sources[0]) :
                  `${filters.sources.length} zdrojů`}
              </span>
              <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 transition ${sourcesDropdownOpen ? "rotate-180" : ""}`} />
            </button>
            {sourcesDropdownOpen && (
              <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-72 overflow-auto">
                <button
                  type="button"
                  onClick={() => { setFilters({ ...filters, sources: [] }); setSourcesDropdownOpen(false); }}
                  className="w-full text-left px-3 py-2 text-xs text-slate-500 hover:bg-slate-50 border-b border-slate-100"
                >
                  Vyčistit výběr (zobrazit vše)
                </button>
                {sources.map((s) => {
                  const checked = filters.sources.includes(s);
                  return (
                    <label key={s} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleSource(s)}
                        className="rounded border-slate-300 text-blue-600 focus:ring-blue-200"
                      />
                      <span className="text-slate-700">{SOURCE_LABELS[s] || s}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
          <div>
            <label className="text-xs text-slate-500">Uživatel</label>
            <select
              value={filters.user}
              onChange={(e) => setFilters({ ...filters, user: e.target.value })}
              className={`min-h-[44px] w-full mt-1 text-sm rounded px-2 py-1.5 transition sm:min-h-0 ${
                filters.user ? "bg-blue-50 text-blue-700 border border-blue-300 font-semibold ring-1 ring-blue-200" : "bg-white border border-slate-300"
              }`}
            >
              <option value="">Všichni</option>
              {users.map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500">Akce</label>
            <select
              value={filters.action}
              onChange={(e) => setFilters({ ...filters, action: e.target.value })}
              className={`min-h-[44px] w-full mt-1 text-sm rounded px-2 py-1.5 transition sm:min-h-0 ${
                filters.action ? "bg-blue-50 text-blue-700 border border-blue-300 font-semibold ring-1 ring-blue-200" : "bg-white border border-slate-300"
              }`}
            >
              <option value="">Všechny</option>
              {actions.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500">Výsledek</label>
            <select
              value={filters.result}
              onChange={(e) => setFilters({ ...filters, result: e.target.value })}
              className="field w-full mt-1"
            >
              <option value="">Vše</option>
              <option value="OK">OK</option>
              <option value="NOK">NOK</option>
              <option value="PENDING">Dispatched (čeká)</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500">Hledat v detailech</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="ID, jméno, ..."
              className="field w-full mt-1"
            />
          </div>
        </div>
      </div>

      {/* Tabulka */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading && (
          <div className="p-4">
            <SkeletonTable rows={10} cols={6} />
          </div>
        )}
        {error && (
          <div className="p-6 bg-rose-50 text-rose-700 text-sm">
            Chyba načítání: {String(error)}
          </div>
        )}
        {!loading && !error && filtered.length === 0 && (
          <div className="p-6 text-center text-slate-500 text-sm">
            Žádné záznamy v zadaném rozsahu.
          </div>
        )}
        {!loading && !error && filtered.length > 0 && uzka && (
          /* 14.9.2026 (fáze 6b): na telefonu seznam řádků. Sedmisloupcová
             tabulka se rolovala do stran a sloupec Detail byl mimo obraz. */
          <div className="divide-y divide-slate-100">
            {filtered.map((e, idx) => {
              const klic = `${e.timestamp}-${idx}`;
              const otevreny = expandedRow === klic;
              const v = getSourceVisual(e.source);
              const IkonaZdroje = v.icon;
              return (
                <div key={klic} className="px-3 py-2.5">
                  <button type="button" onClick={() => setExpandedRow(otevreny ? null : klic)}
                    aria-expanded={otevreny} className="flex w-full items-start gap-2 text-left">
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-mono text-[13px] font-semibold text-slate-900">{e.action}</span>
                      <span className="block truncate text-[12px] text-slate-500">
                        {formatDateTime(e.timestamp)}
                        {e.user ? `, ${e.user === "cron" ? "cron" : e.user}` : ""}
                        {e.durationMs != null ? `, ${formatDuration(e.durationMs)}` : ""}
                      </span>
                    </span>
                    {e.result === "OK" && <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />}
                    {e.result === "NOK" && <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-rose-500" />}
                    {e.result === "PENDING" && <Clock className="mt-0.5 h-5 w-5 shrink-0 text-sky-600" />}
                  </button>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    <span className={`inline-flex items-center gap-1 rounded-lg border px-2 py-0.5 text-[12px] font-medium ${v.bg} ${v.text} ${v.border}`}>
                      <IkonaZdroje className="h-3 w-3" />
                      {SOURCE_LABELS[e.source] || e.source}
                    </span>
                  </div>
                  {otevreny && (
                    <div className="mt-2">
                      {e.error && <div className="mb-2 text-sm text-rose-600"><strong>Chyba:</strong> {e.error}</div>}
                      <pre className="overflow-x-auto rounded bg-slate-900 p-3 text-xs text-slate-100">
{JSON.stringify(e.details, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {!loading && !error && filtered.length > 0 && !uzka && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="px-3 py-2 text-left">Čas</th>
                  <th className="px-3 py-2 text-left">Zdroj</th>
                  <th className="px-3 py-2 text-left">Akce</th>
                  <th className="px-3 py-2 text-left">Uživatel</th>
                  <th className="px-3 py-2 text-left">Výsledek</th>
                  <th className="px-3 py-2 text-right">Doba</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((e, idx) => {
                  const rowKey = `${e.timestamp}-${idx}`;
                  const expanded = expandedRow === rowKey;
                  return (
                    <>
                      <tr
                        key={rowKey}
                        className="hover:bg-slate-50 cursor-pointer"
                        onClick={() => setExpandedRow(expanded ? null : rowKey)}
                      >
                        <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-700">{formatDateTime(e.timestamp)}</td>
                        <td className="px-3 py-2">
                          {(() => {
                            const v = getSourceVisual(e.source);
                            const Icon = v.icon;
                            return (
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-medium border ${v.bg} ${v.text} ${v.border}`}>
                                <Icon className="w-3 h-3" />
                                {SOURCE_LABELS[e.source] || e.source}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs text-slate-600">{e.action}</td>
                        <td className="px-3 py-2 text-xs text-slate-600">{e.user === "cron" ? <span className="italic text-slate-400">cron</span> : e.user}</td>
                        <td className="px-3 py-2">
                          {e.result === "OK" && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-medium">
                              <CheckCircle2 className="w-3.5 h-3.5" /> OK
                            </span>
                          )}
                          {e.result === "NOK" && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-lg bg-rose-50 text-rose-700 text-xs font-medium">
                              <XCircle className="w-3.5 h-3.5" /> NOK
                            </span>
                          )}
                          {e.result === "PENDING" && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-lg bg-blue-50 text-blue-700 text-xs font-medium" title="Background dispatch, výsledek běhu se loguje samostatně po dokončení">
                              <Clock className="w-3.5 h-3.5" /> Dispatched
                            </span>
                          )}
                          {!e.result && <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-3 py-2 text-right text-slate-600">{formatDuration(e.durationMs)}</td>
                        <td className="px-3 py-2 text-right">
                          <button
                            className="text-xs text-slate-500 hover:text-slate-700"
                            onClick={(ev) => { ev.stopPropagation(); setExpandedRow(expanded ? null : rowKey); }}
                          >
                            {expanded ? "Skrýt" : "Detail"}
                          </button>
                        </td>
                      </tr>
                      {expanded && (
                        <tr key={rowKey + "-detail"} className="bg-slate-50">
                          <td colSpan={7} className="px-3 py-3">
                            {e.error && (
                              <div className="mb-2 text-rose-600 text-sm">
                                <strong>Chyba:</strong> {e.error}
                              </div>
                            )}
                            <pre className="text-xs bg-slate-900 text-slate-100 rounded p-3 overflow-x-auto">
{JSON.stringify(e.details, null, 2)}
                            </pre>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="text-xs text-slate-400 text-center">
        Zobrazeno {filtered.length} z {entries.length} záznamů. Retence 90 dní (Netlify Blobs).
      </div>
    </Stranka>
  );
}
